import React, { useEffect, useState } from "react";
import { colors } from "@styles/colors";
import { useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";
import { Stack, Text, YStack, XStack, Circle, Popover, Button } from "tamagui";
import { RiArrowDropDownLine } from "react-icons/ri";
import { getColor } from "@utils/func/colorTool";
import { getFirstText } from "@utils/func/textTool";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse } from "axios";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { IdentityContext, IdentityMember, useUser } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { pickChooseIdentity } from "@utils/func/identity";
import { OA_ENTRY_PREFERRED_CONTEXT_KEY } from "@utils/line/oaEntryCheck";
import { track } from "@lib/tracker";

/**
 * @param haze 覆寫「這個頁首是不是淡底」。預設依路徑判斷（Skin V2 的頁），
 *   但首頁的冷啟動 hero 不套 V2 骨架、淡霧與否由 HomePage/homeSkin.ts 決定，
 *   同一條路徑下兩種 hero 都可能出現，所以只能由呼叫端指定。
 */
export function IdentitySwitchMenu({ haze: hazeOverride }: { haze?: boolean } = {}) {
  // 淡霧頁首：原本的半透明白膠囊在淡底上等於消失，改成實白小卡 + 深藍字
  const routeHaze = useHazePage();
  const haze = hazeOverride ?? routeHaze;
  const { cusJwtToken } = useCusAuth();
  const { setPersonalID, chooseIdentity, setChooseIdentity } = useUser();
  const { clearProducts } = useOrder();
  const [identityList, setIdentityList] = useState<IdentityContext[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!cusJwtToken) return;
    handleRequest({
      asyncRequest: () =>
        api.get("/app-api/customer/contexts", {
          headers: { Authorization: `Bearer ${cusJwtToken}` },
        }),
      successCallback: (res: AxiosResponse) => {
        const { data } = res.data;
        if (data?.contexts?.length > 0) {
          const contexts = data.contexts as IdentityContext[];
          // v0.7：personal context 用 max_members === 1 判斷（取代 type === "persona"）
          const personaContext = contexts.find((item) => item.max_members === 1);

          //-- 選擇順位：1.OA 入口偏好身分（一次性） 2.沿用已選身分 3.API 預設 4.第一筆
          //（contexts API 不含這兩欄，直接覆蓋會清掉地址，導致下游取不到 customer_address_id）
          // 必須讀 store 當下最新值：本 effect 在掛載時就發出請求，closure 內的 chooseIdentity 是
          // 「還沒有地址」的舊值；kiosk bootstrap 常在本請求回來前就把 address/choose_address 寫好，
          // 若拿舊值去 pick，等於用無地址的版本覆蓋掉剛同步的地址 → 首次進頁顯示「尚未設定地址」
          // （reload 後因已持久化才看似正常）。
          // preferredContextId：LineLoginProvider 在 OA 入口帳戶檢查 has_account 時寫入，讀完即清（一次性），
          // 讀不到（null）時完全不影響原優先序。
          let preferredContextId: string | null = null;
          try {
            preferredContextId = sessionStorage.getItem(OA_ENTRY_PREFERRED_CONTEXT_KEY);
          } catch {
            // 忽略：取不到就當作沒有偏好身分
          }
          const thisChooseIdentity = pickChooseIdentity(contexts, {
            currentChooseIdentity: useUser.getState().chooseIdentity,
            defaultContextId: data?.default_context_id,
            preferredContextId,
          });
          if (preferredContextId != null) {
            try {
              sessionStorage.removeItem(OA_ENTRY_PREFERRED_CONTEXT_KEY);
            } catch {
              // 忽略：清不掉頂多下次再套用一次，不影響正確性
            }
          }

          setPersonalID(personaContext ? String(personaContext.context_id) : null);
          if (thisChooseIdentity) setChooseIdentity(thisChooseIdentity);
          setIdentityList(contexts);
        } else {
          // 無身分：放行首頁 loading，避免永遠轉圈
          useUtilsStore.getState().setLoading(false);
        }
      },
      errorCallback: (error: unknown) => {
        console.error("fetch contexts err", error);
        // contexts 失敗時也要關品牌 loading，否則 HomePage 會一直等身分
        useUtilsStore.getState().setLoading(false);
      },
    });
    // 只依 token 抓一次清單。勿把 chooseIdentity?.context_id 放進 deps：
    // 身分從 null→有值時會再打一輪 contexts，疊上 HomePage 換身分重抓 /home，
    // 造成進首頁連續跳出好幾次鋼瓶 loading。
  }, [cusJwtToken, setPersonalID, setChooseIdentity]);

  //@ 切換身分
  const handleSwitchIdentity = (item: IdentityContext) => {
    if (item.context_id !== chooseIdentity?.context_id) {
      track("identity_switch_select", { from: chooseIdentity?.context_id ?? null, to: item.context_id });
      setChooseIdentity(item);
      clearProducts();
    }
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen} placement="bottom-start" allowFlip>
        <Popover.Trigger asChild>
          <Button
            backgroundColor={haze ? "#fff" : "rgba(255, 255, 255, 0.2)"}
            borderRadius={20}
            px={10}
            h={40}
            ai="center"
            hoverStyle={{ backgroundColor: haze ? "#f7fbff" : "rgba(255, 255, 255, 0.2)" }}
            pressStyle={{ backgroundColor: haze ? "#f2f7fd" : "rgba(255, 255, 255, 0.2)", opacity: 0.8 }}
            borderColor={haze ? v2.cardBorder : "rgba(255,255,255,0.2)"}
            borderWidth={1}>
            <XStack ai="center" gap={8}>
              <Circle size={20} backgroundColor={haze ? v2.primarySoft : "rgba(255, 255, 255, 0.5)"} pressStyle={{ opacity: 0.8 }}>
                <Text color={haze ? v2.hazeInk : "white"} fontSize={12} fow="800">
                  {getFirstText(chooseIdentity?.context_name || "")}
                </Text>
              </Circle>
              <Text col={haze ? v2.hazeInk : "white"} fontSize={14} fontWeight="bold">
                {chooseIdentity?.context_name || ""}
              </Text>
              <RiArrowDropDownLine color={haze ? v2.hazeInk : "white"} style={{ transform: open ? "rotate(180deg)" : "none", transition: "0.2s" }} />
            </XStack>
          </Button>
        </Popover.Trigger>
        <Popover.Content
          borderWidth={0}
          mt={8}
          p={4}
          backgroundColor="white"
          borderRadius={20}
          elevation={15}
          enterStyle={{ y: -10, opacity: 0 }}
          exitStyle={{ y: -10, opacity: 0 }}
          animation={[
            "quick",
            {
              opacity: { overshootClamping: true },
            },
          ]}>
          <Popover.Arrow borderWidth={0} backgroundColor="white" />
          <YStack gap={4}>
            {identityList.map((item: IdentityContext) => {
              const isActive = chooseIdentity?.context_id === item?.context_id;
              return (
                <XStack
                  key={item.context_id}
                  p={12}
                  borderRadius={18}
                  ai="center"
                  gap={15}
                  backgroundColor={isActive ? colors.primaryActive : "transparent"}
                  hoverStyle={{ backgroundColor: colors.primaryHover }}
                  onPress={() => {
                    handleSwitchIdentity(item);
                  }}>
                  <Stack w={30} h={30} borderRadius={50} ai="center" jc="center" backgroundColor={isActive ? colors.primary : colors.primaryActive}>
                    <Text color={isActive ? "white" : colors.grayDark} fontSize={16} fontWeight="700">
                      {getFirstText(item.context_name || "")}
                    </Text>
                  </Stack>
                  <YStack>
                    <Text fontSize={16} fontWeight="700" color={isActive ? colors.primary : colors.grayDark}>
                      {item.context_name || "..."}
                    </Text>
                  </YStack>
                </XStack>
              );
            })}

          </YStack>
        </Popover.Content>
      </Popover>
    </>
  );
}
export const MemberCircleList = ({ haze: hazeOverride }: { haze?: boolean } = {}) => {
  // 頭像外框在淡霧底上要用頁面底色，白色外框在淡藍白上看不出分隔
  const routeHaze = useHazePage();
  const haze = hazeOverride ?? routeHaze;
  const { chooseIdentity } = useUser();
  return (
    <XStack>
      {chooseIdentity?.members?.map((item: IdentityMember, i: number) => {
        return (
          <Stack
            key={`member-${i}`}
            width={28}
            height={28}
            borderRadius={14}
            backgroundColor={getColor(i).bgColor}
            alignItems="center"
            justifyContent="center"
            marginLeft={i === 0 ? 0 : -9}
            borderWidth={2}
            borderColor={haze ? "#f8fafc" : "#fff"}
            zIndex={10 - i}>
            <Text fontSize={11} fontWeight="700" color={getColor(i).color}>
              {getFirstText(item?.customer_name)}
            </Text>
          </Stack>
        );
      })}
    </XStack>
  );
};
